<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import gsap from 'gsap';

const props = defineProps({
  tag: {
    type: String,
    default: 'div',
  },
  threshold: {
    type: Number,
    default: 0.05,
  },
  rootMargin: {
    type: String,
    default: '0px 0px -5% 0px',
  },
  y: {
    type: Number,
    default: 50,
  },
  blur: {
    type: Number,
    default: 12,
  },
  duration: {
    type: Number,
    default: 0.5,
  },
  ease: {
    type: String,
    default: 'power2.out',
  },
  resetOnLeave: {
    type: Boolean,
    default: true,
  },
});

const rootRef = ref(null);
let observer;

const getHiddenState = () => ({
  y: props.y,
  opacity: 0,
  filter: `blur(${props.blur}px)`,
});

const getVisibleState = () => ({
  y: 0,
  opacity: 1,
  filter: 'blur(0px)',
});

const playReveal = () => {
  if (!rootRef.value) return;

  gsap.fromTo(rootRef.value, getHiddenState(), {
    ...getVisibleState(),
    duration: props.duration,
    ease: props.ease,
    overwrite: true,
  });
};

const resetReveal = () => {
  if (!rootRef.value) return;
  gsap.set(rootRef.value, getHiddenState());
};

onMounted(() => {
  if (!rootRef.value) return;

  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    gsap.set(rootRef.value, getVisibleState());
    return;
  }

  resetReveal();
  observer = new IntersectionObserver(
    ([entry]) => {
      if (entry?.isIntersecting) {
        playReveal();
        return;
      }

      if (props.resetOnLeave) {
        resetReveal();
      }
    },
    {
      threshold: props.threshold,
      rootMargin: props.rootMargin,
    }
  );

  observer.observe(rootRef.value);
});

onBeforeUnmount(() => {
  observer?.disconnect();
});
</script>

<template>
  <component :is="tag" ref="rootRef">
    <slot />
  </component>
</template>
